import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DictationField from "@/components/DictationField";
import { useServiceLines } from "@/lib/serviceLines";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TeamMemberCombobox from "@/components/TeamMemberCombobox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Briefcase, ShieldAlert, Plus, X, Download, ChevronDown, Upload } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api, { API_BASE, TOKEN_KEY, formatApiErrorDetail } from "../lib/api";
import { formatDate } from "../lib/dateFormat";
import { useAuth } from "../context/AuthContext";
import { StatusPill } from "../components/StatusPill";
import { HighRiskBadge } from "../components/HighRiskBadge";
import BulkImportCasesDialog from "./admin/BulkImportCasesDialog";
import BulkUpdateCasesDialog from "./admin/BulkUpdateCasesDialog";

const STATUSES = [
  "New",
  "In Progress",
  "Under Investigation",
  "Pending Follow-up",
  "Escalated",
  "Closed",
];

const CASE_SOURCES = [
  "Manager Report",
  "Anonymous Complaint",
  "Hotline",
  "HR Observation",
  "Compliance Concern",
  "Employee Grievance",
];

function KpiCard({ title, value, icon: Icon, accent, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="brand-card group flex flex-1 items-center gap-4 px-5 py-4 text-left transition-all"
      style={{
        borderColor: active ? "var(--border-strong)" : "var(--border-subtle)",
        boxShadow: active
          ? "var(--inset-highlight), 0 0 0 1px var(--border-strong)"
          : "var(--inset-highlight)",
      }}
    >
      <div
        className="flex h-9 w-9 items-center justify-center rounded-lg"
        style={{
          backgroundColor: accent,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
        }}
      >
        <Icon className="h-4 w-4 text-white" strokeWidth={1.8} />
      </div>
      <div className="flex-1">
        <p
          className="text-[10px] font-semibold uppercase tracking-[0.10em]"
          style={{ color: "var(--page-fg-secondary)" }}
        >
          {title}
        </p>
        <p
          className="mt-0.5 text-2xl font-semibold tabular-nums"
          style={{ color: "var(--page-fg)", letterSpacing: "-0.01em" }}
        >
          {value}
        </p>
      </div>
      {active && (
        <span
          className="rounded-md px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
          style={{
            backgroundColor: "rgba(30,58,95,0.10)",
            color: "#1E3A5F",
          }}
        >
          Filtered
        </span>
      )}
    </button>
  );
}

// ---------- Phase 5 — My Stale Queue banner (HR partners only) ----------

function MyStaleBanner({ currentUserId }) {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/cases/my-stale");
      setData(data);
    } catch {
      setData({ items: [], stale_threshold_days: 0 });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  if (!data) return null;
  const items = data.items || [];
  if (items.length === 0) return null;

  const handleReviewed = async (caseId) => {
    setBusyId(caseId);
    try {
      await api.post(`/cases/${caseId}/mark-reviewed`, {
        notes: "Reviewed from My Stale Queue banner",
      });
      toast.success("Marked reviewed — staleness clock reset");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      data-testid="my-stale-banner"
      className="brand-card mb-4 p-4"
      style={{
        backgroundColor: "rgba(245, 217, 10, 0.04)",
        borderColor: "rgba(245, 217, 10, 0.30)",
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ backgroundColor: "#F5D90A", color: "#0B0B0F" }}
          >
            <ShieldAlert className="h-4 w-4" strokeWidth={1.8} />
          </span>
          <div>
            <p
              className="text-sm font-semibold"
              style={{ color: "var(--page-fg)" }}
            >
              My Stale Queue · {items.length} case{items.length === 1 ? "" : "s"} need attention
            </p>
            <p
              className="text-[11px]"
              style={{ color: "var(--page-fg-secondary)" }}
            >
              No activity in {data.stale_threshold_days}+ days · oldest first
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="my-stale-toggle"
          onClick={() => setCollapsed((v) => !v)}
          className="rounded-md px-3 py-1 text-xs"
          style={{
            border: "1px solid var(--border-subtle)",
            color: "var(--page-fg-secondary)",
          }}
        >
          {collapsed ? "Show" : "Hide"}
        </button>
      </div>

      {!collapsed && (
        <ul className="mt-3 space-y-1">
          {items.slice(0, 8).map((c) => (
            <li
              key={c.id}
              data-testid={`my-stale-row-${c.case_id}`}
              className="flex items-center gap-2 rounded-lg px-3 py-2"
              style={{
                backgroundColor: "var(--surface-2)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              <span
                className="font-mono text-xs tabular-nums"
                style={{ color: "var(--page-fg)" }}
              >
                {c.case_id}
              </span>
              <span
                className="flex-1 truncate text-sm"
                style={{ color: "var(--page-fg)" }}
              >
                {c.issue_type} — {c.specific_issue}
                {c.high_risk && (
                  <span
                    className="ml-2 inline-block rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    style={{
                      backgroundColor: "rgba(229,72,77,0.10)",
                      color: "#B42318",
                    }}
                  >
                    High-risk
                  </span>
                )}
              </span>
              <span
                className="text-[11px] tabular-nums"
                style={{ color: "var(--page-fg-muted)" }}
              >
                {c.last_activity ? `last: ${c.last_activity.slice(0, 10)}` : "no activity"}
              </span>
              <Button
                variant="outline"
                size="sm"
                data-testid={`my-stale-open-${c.case_id}`}
                onClick={() => navigate(`/cases/${c.id}`)}
                className="h-7 rounded-md text-xs"
                style={{
                  borderColor: "var(--border-strong)",
                  color: "var(--page-fg)",
                }}
              >
                Open
              </Button>
              <Button
                size="sm"
                disabled={busyId === c.id}
                data-testid={`my-stale-reviewed-${c.case_id}`}
                onClick={() => handleReviewed(c.id)}
                className="h-7 rounded-md text-xs font-semibold text-white"
                style={{
                  background: "linear-gradient(180deg, #234876 0%, #1E3A5F 100%)",
                }}
              >
                {busyId === c.id ? "…" : "Mark reviewed"}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}



function CreateCaseDialog({ teamMembers, taxonomy, hrPartners, onCreated, currentUserId, currentUserIsHR }) {
  const { nameOf } = useServiceLines();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    team_member_id: "",
    date_reported: today,
    reported_by: "",
    source: "Manager Report",
    issue_type: "Conduct",
    specific_issue: "",
    case_details: "",
    status: "New",
    assigned_hr_partner_id: "",
    // Phase 2 (Feb 2026) — HR discussion date (optional, ISO). Field is
    // permissive on the backend up to +7 days from today; empty string on
    // the form is treated as "not set" at submit time.
    date_of_discussion: "",
  });

  const issueTypes = Object.keys(taxonomy || {});
  const specificOptions = taxonomy?.[form.issue_type] || [];

  // Phase 7a — load active case templates so HR can prefill the form.
  React.useEffect(() => {
    let cancelled = false;
    if (!open) return;
    api
      .get("/case-templates")
      .then(({ data }) => {
        if (!cancelled) setTemplates(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setActiveTemplateId(id);
    setForm((f) => ({
      ...f,
      issue_type: t.issue_type || f.issue_type,
      specific_issue: t.specific_issue || "",
      case_details: t.case_details_template || "",
      status:
        t.default_status && ["New", "In Progress", "Under Investigation"].includes(t.default_status)
          ? t.default_status
          : f.status,
    }));
    toast.success(`Template applied: ${t.name}`);
  };

  const update = (k) => (v) => {
    if (k === "issue_type") {
      const next = taxonomy?.[v] || [];
      // Phase 2 (Feb 2026) — dynamic taxonomy: reset specific_issue when the
      // parent changes. If the new parent has options, prefill the first;
      // otherwise leave blank so the free-text input starts empty.
      setForm((f) => ({ ...f, issue_type: v, specific_issue: next[0] || "" }));
    } else {
      setForm((f) => ({ ...f, [k]: v }));
    }
  };

  React.useEffect(() => {
    if (form.specific_issue === "" && specificOptions.length > 0) {
      setForm((f) => ({ ...f, specific_issue: specificOptions[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specificOptions.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.team_member_id) {
      toast.error("Select a team member");
      return;
    }
    setSubmitting(true);
    try {
      const body = { ...form };
      // Phase 2 (Feb 2026) — omit blank date_of_discussion so the server
      // stores None rather than an empty string.
      if (!body.date_of_discussion) delete body.date_of_discussion;
      // Phase 5: special sentinel "__self__" → assign to the requesting HR user.
      // Empty/null → server runs workload-balanced auto-assign.
      if (body.assigned_hr_partner_id === "__self__") {
        body.assigned_hr_partner_id = currentUserId || null;
      } else if (!body.assigned_hr_partner_id) {
        delete body.assigned_hr_partner_id;
      }
      // Phase 7a — surface the chosen template to the server for audit trail
      // (server-side merge already filled in the form values, but the server
      // still records the originating template_id in the audit log).
      if (activeTemplateId) {
        body.template_id = activeTemplateId;
      }
      const { data } = await api.post("/cases", body);
      const summary = data.related_cases_summary || {};
      const sameEmp = summary.same_employee_count || 0;
      if (sameEmp > 0) {
        toast.success(
          `Case ${data.case_id} created · ${sameEmp} prior case${sameEmp === 1 ? "" : "s"} on this employee in last 6 months`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Case ${data.case_id} created`);
      }
      if (data.auto_assignment_reason) {
        toast.info(data.auto_assignment_reason, { duration: 5000 });
      }
      setOpen(false);
      setForm((f) => ({ ...f, specific_issue: "", case_details: "", reported_by: "" }));
      setActiveTemplateId("");
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="new-case-button"
          className="h-9 rounded-lg text-sm font-semibold text-white"
          style={{
            background: "linear-gradient(180deg, #234876 0%, #1E3A5F 100%)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.10), 0 1px 0 rgba(15,15,20,0.04)",
          }}
        >
          <Plus className="mr-1 h-4 w-4" strokeWidth={2} />
          New case
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl rounded-2xl">
        <DialogHeader>
          <DialogTitle>Create new case</DialogTitle>
          <DialogDescription>
            Harassment/Discrimination and Safety cases are automatically flagged as high-risk.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="create-case-form">
          {templates.length > 0 && (
            <div className="col-span-2 space-y-1.5 rounded-xl border border-brand-tealMist bg-[color:var(--surface-2)] p-3">
              <Label className="text-xs font-medium text-brand-teal">
                Use a template (optional)
              </Label>
              <Select value={activeTemplateId} onValueChange={applyTemplate}>
                <SelectTrigger
                  data-testid="case-template-select"
                  className="bg-white"
                >
                  <SelectValue placeholder="Pick a template to pre-fill this form" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem
                      key={t.id}
                      value={t.id}
                      data-testid={`case-template-option-${t.id}`}
                    >
                      {t.name} · {t.issue_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                Templates are managed in Admin → Templates.
              </p>
            </div>
          )}
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="case-team-member-trigger">Team member</Label>
            <TeamMemberCombobox
              members={teamMembers}
              value={form.team_member_id}
              onChange={update("team_member_id")}
              serviceLineLabel={nameOf}
              testId="case-team-member"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Date reported</Label>
            <Input
              type="date"
              value={form.date_reported}
              onChange={(e) => update("date_reported")(e.target.value)}
              data-testid="case-date-reported-input"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>Date of discussion</Label>
            <Input
              type="date"
              value={form.date_of_discussion}
              onChange={(e) => update("date_of_discussion")(e.target.value)}
              data-testid="case-date-of-discussion-input"
              aria-label="Date HR met with the employee"
            />
            <p className="text-[11px] text-[color:var(--page-fg-muted)]">
              Optional — when HR met with the employee. Can be up to 7 days in the future.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Reported by</Label>
            <Input
              value={form.reported_by}
              onChange={(e) => update("reported_by")(e.target.value)}
              data-testid="case-reported-by-input"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>Source</Label>
            <Select value={form.source} onValueChange={update("source")}>
              <SelectTrigger data-testid="case-source-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CASE_SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Issue type</Label>
            <Select value={form.issue_type} onValueChange={update("issue_type")}>
              <SelectTrigger data-testid="case-issue-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {issueTypes.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Specific issue</Label>
            {specificOptions.length > 0 ? (
              <Select
                value={form.specific_issue}
                onValueChange={update("specific_issue")}
              >
                <SelectTrigger data-testid="case-specific-issue-select">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {specificOptions.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <>
                <Input
                  data-testid="case-specific-issue-input"
                  value={form.specific_issue}
                  onChange={(e) => update("specific_issue")(e.target.value)}
                  placeholder="Describe the specific issue"
                  required
                />
                <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                  No subcategories defined for this issue type — enter freely. Admins can add subcategories via Admin → Taxonomy.
                </p>
              </>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Initial status</Label>
            <Select value={form.status} onValueChange={update("status")}>
              <SelectTrigger data-testid="case-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["New", "In Progress", "Under Investigation"].map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Assigned HR partner</Label>
            <Select
              value={form.assigned_hr_partner_id || "__none__"}
              onValueChange={(v) =>
                update("assigned_hr_partner_id")(v === "__none__" ? "" : v)
              }
            >
              <SelectTrigger data-testid="case-assigned-partner-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" data-testid="case-assigned-unassigned">
                  Unassigned (default)
                </SelectItem>
                {currentUserIsHR && currentUserId && (
                  <SelectItem value="__self__" data-testid="case-assigned-self">
                    Assign to me
                  </SelectItem>
                )}
                {hrPartners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[color:var(--page-fg-muted)]">
              Leave unassigned to review it later. Unassigned cases don&apos;t trigger HR-partner email notifications.
            </p>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Case details</Label>
            <DictationField
              rows={4}
              value={form.case_details}
              onChange={(e) => update("case_details")(e.target.value)}
              data-testid="case-details-textarea"
              testidPrefix="case-details"
              targetField="case_details"
            />
            <p className="text-[11px] text-[color:var(--page-fg-muted)]">
              Optional. Add now or edit later on the case detail page.
            </p>
          </div>
          <DialogFooter className="col-span-2">
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="create-case-submit-button"
              className="bg-brand-teal text-white hover:bg-brand-tealDark"
            >
              {submitting ? "Creating…" : "Create case"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Cases() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [hrPartners, setHrPartners] = useState([]);
  const [taxonomy, setTaxonomy] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [filter, setFilter] = useState({
    mine: searchParams.get("mine") === "1",
    highRisk:
      searchParams.get("high_risk") === "1" ||
      searchParams.get("high_risk") === "true",
    status: searchParams.get("status") || "",
    service_lines: (searchParams.get("service_lines") || "").split(",").filter(Boolean),
    issue_types: (searchParams.get("issue_types") || "").split(",").filter(Boolean),
    statuses: (searchParams.get("statuses") || "").split(",").filter(Boolean),
    start_date: searchParams.get("start_date") || "",
    end_date: searchParams.get("end_date") || "",
    file_number: searchParams.get("file_number") || "",
    sla_breach: searchParams.get("sla_breach") === "1",
    // Phase 4c — server-evaluated drill filters
    overdue: searchParams.get("overdue") === "true",
    follow_up_due: searchParams.get("follow_up_due") || "",
    stale: searchParams.get("stale") === "true",
    assigned_hr_partner_id: searchParams.get("assigned_hr_partner_id") || "",
    department: searchParams.get("department") || "",
    import_batch_id: searchParams.get("import_batch_id") || "",
    legacy_case_id: searchParams.get("legacy_case_id") || "",
  });
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);

  const isHR = user?.role === "hr";
  const canSeeHighRisk = isHR || user?.role === "compliance";
  const canExport = isHR || user?.role === "compliance";
  // Phase 10 — "archived only" view (HR / Compliance / Legal Review only).
  // Phase 2 (May 2026) — also support `include_archived=true` which shows
  // archived + active together (the user-facing "Show archived" toggle).
  const canSeeArchived = isHR || user?.role === "compliance" || user?.role === "legal_review";
  const archivedView = searchParams.get("archived") === "true";
  const includeArchivedView = searchParams.get("include_archived") === "true";
  const searchParamsKey = searchParams.toString();

  const load = async () => {
    setLoading(true);
    try {
      const qsForFilter = searchParams.toString();
      const [casesRes, tmRes, partnersRes, taxRes] = await Promise.all([
        api.get(`/cases${qsForFilter ? `?${qsForFilter}` : ""}`),
        api.get("/team-members"),
        api.get("/config/hr-partners").catch(() => ({ data: [] })),
        api.get("/config/issue-taxonomy").catch(() => ({ data: { taxonomy: {} } })),
      ]);
      setCases(casesRes.data);
      setTeamMembers(tmRes.data);
      setHrPartners(partnersRes.data || []);
      setTaxonomy(taxRes.data?.taxonomy || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsKey]);

  // Sync URL → state whenever URL changes from outside (dashboard drill-downs, topbar search, browser back).
  useEffect(() => {
    const fromUrl = {
      mine: searchParams.get("mine") === "1",
      highRisk:
        searchParams.get("high_risk") === "1" ||
        searchParams.get("high_risk") === "true",
      status: searchParams.get("status") || "",
      service_lines: (searchParams.get("service_lines") || "").split(",").filter(Boolean),
      issue_types: (searchParams.get("issue_types") || "").split(",").filter(Boolean),
      statuses: (searchParams.get("statuses") || "").split(",").filter(Boolean),
      start_date: searchParams.get("start_date") || "",
      end_date: searchParams.get("end_date") || "",
      file_number: searchParams.get("file_number") || "",
      sla_breach: searchParams.get("sla_breach") === "1",
      overdue: searchParams.get("overdue") === "true",
      follow_up_due: searchParams.get("follow_up_due") || "",
      stale: searchParams.get("stale") === "true",
      assigned_hr_partner_id: searchParams.get("assigned_hr_partner_id") || "",
      department: searchParams.get("department") || "",
    };
    setFilter((prev) =>
      JSON.stringify(prev) === JSON.stringify(fromUrl) ? prev : fromUrl
    );
    setQ((prev) => {
      const urlQ = searchParams.get("q") || "";
      return prev === urlQ ? prev : urlQ;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsKey]);

  useEffect(() => {
    const p = {};
    if (q) p.q = q;
    if (filter.mine) p.mine = "1";
    if (filter.highRisk) p.high_risk = "1";
    if (filter.status) p.status = filter.status;
    if (filter.service_lines?.length) p.service_lines = filter.service_lines.join(",");
    if (filter.issue_types?.length) p.issue_types = filter.issue_types.join(",");
    if (filter.statuses?.length) p.statuses = filter.statuses.join(",");
    if (filter.start_date) p.start_date = filter.start_date;
    if (filter.end_date) p.end_date = filter.end_date;
    if (filter.file_number) p.file_number = filter.file_number;
    if (filter.sla_breach) p.sla_breach = "1";
    if (filter.overdue) p.overdue = "true";
    if (filter.follow_up_due) p.follow_up_due = filter.follow_up_due;
    if (filter.stale) p.stale = "true";
    if (filter.assigned_hr_partner_id) p.assigned_hr_partner_id = filter.assigned_hr_partner_id;
    if (filter.department) p.department = filter.department;
    if (filter.import_batch_id) p.import_batch_id = filter.import_batch_id;
    if (filter.legacy_case_id) p.legacy_case_id = filter.legacy_case_id;
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, filter]);

  const filtered = useMemo(() => {
    let list = cases;
    if (filter.mine && isHR) {
      list = list.filter((c) => c.assigned_hr_partner_id === user?.id);
    }
    if (filter.highRisk) {
      list = list.filter((c) => c.high_risk);
    }
    if (filter.status) {
      list = list.filter((c) => c.status === filter.status);
    }
    if (filter.service_lines?.length) {
      const wanted = new Set(filter.service_lines);
      list = list.filter((c) => wanted.has(c.team_member_service_line) || wanted.has(c.service_line));
    }
    if (filter.issue_types?.length) {
      const wanted = new Set(filter.issue_types);
      list = list.filter((c) => wanted.has(c.issue_type));
    }
    if (filter.statuses?.length) {
      const wanted = new Set(filter.statuses);
      list = list.filter((c) => wanted.has(c.status));
    }
    if (filter.start_date) {
      list = list.filter((c) => (c.date_reported || "") >= filter.start_date);
    }
    if (filter.end_date) {
      // end_date is inclusive of the day
      const endUpper = `${filter.end_date}T23:59:59.999`;
      list = list.filter((c) => (c.date_reported || "") <= endUpper);
    }
    if (filter.file_number) {
      list = list.filter((c) => String(c.team_member_file_number) === String(filter.file_number));
    }
    if (filter.sla_breach) {
      const cutoffMs = Date.now() - 30 * 24 * 60 * 60 * 1000;
      list = list.filter((c) => {
        if (c.status === "Closed") return false;
        const t = Date.parse(c.date_reported || "");
        return !Number.isNaN(t) && t <= cutoffMs;
      });
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((c) =>
        [c.case_id, c.team_member_name, c.specific_issue, c.issue_type, c.status]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle))
      );
    }
    return list;
  }, [cases, q, filter, user, isHR]);

  const myActiveCount = useMemo(() => {
    if (!isHR) return 0;
    return cases.filter(
      (c) => c.assigned_hr_partner_id === user?.id && c.status !== "Closed"
    ).length;
  }, [cases, user, isHR]);

  const highRiskCount = useMemo(
    () => cases.filter((c) => c.high_risk && c.status !== "Closed").length,
    [cases]
  );

  const toggleMine = () => setFilter((f) => ({ ...f, mine: !f.mine }));
  const toggleHighRisk = () => setFilter((f) => ({ ...f, highRisk: !f.highRisk }));

  const clearFilters = () => {
    setQ("");
    setFilter({
      mine: false,
      highRisk: false,
      status: "",
      service_lines: [],
      issue_types: [],
      statuses: [],
      start_date: "",
      end_date: "",
      file_number: "",
      sla_breach: false,
    });
  };

  const activeFilterCount =
    (filter.mine ? 1 : 0) +
    (filter.highRisk ? 1 : 0) +
    (filter.status ? 1 : 0) +
    (q ? 1 : 0) +
    (filter.service_lines?.length ? 1 : 0) +
    (filter.issue_types?.length ? 1 : 0) +
    (filter.statuses?.length ? 1 : 0) +
    (filter.start_date ? 1 : 0) +
    (filter.end_date ? 1 : 0) +
    (filter.file_number ? 1 : 0) +
    (filter.sla_breach ? 1 : 0);

  const handleExport = async (format = "csv") => {
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (filter.service_lines?.length) qs.set("service_lines", filter.service_lines.join(","));
      if (filter.issue_types?.length) qs.set("issue_types", filter.issue_types.join(","));
      if (filter.statuses?.length) qs.set("statuses", filter.statuses.join(","));
      if (filter.status && !filter.statuses?.length) qs.set("statuses", filter.status);
      const ext = format === "xlsx" ? "xlsx" : "csv";
      const url = `${API_BASE}/cases/export.${ext}${qs.toString() ? "?" + qs.toString() : ""}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `er_cases_${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(href);
      toast.success(`Exported ${filtered.length} cases to ${ext.toUpperCase()}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
      setExportMenuOpen(false);
    }
  };

  return (
    <div data-testid="cases-page" className="mx-auto max-w-7xl px-6 py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1
            className="font-serif text-3xl font-semibold tracking-tight"
            style={{ letterSpacing: "-0.015em", color: "var(--page-fg)" }}
          >
            Cases
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--page-fg-secondary)" }}>
            {user?.role === "manager"
              ? `Viewing cases for your assigned team (position IDs ${user.manages_file_numbers?.join(", ") || "—"}).`
              : "All Employee Relations cases."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        {isHR && user?.is_admin && (
          <>
            <Button
              variant="outline"
              onClick={() => setBulkImportOpen(true)}
              data-testid="bulk-import-cases-button"
              className="h-9"
            >
              <Upload className="mr-1 h-4 w-4" />
              Bulk import
            </Button>
            <Button
              variant="outline"
              onClick={() => setBulkUpdateOpen(true)}
              data-testid="bulk-update-cases-button"
              className="h-9"
            >
              <Upload className="mr-1 h-4 w-4" />
              Bulk update
            </Button>
            <BulkImportCasesDialog
              open={bulkImportOpen}
              onOpenChange={setBulkImportOpen}
              onImportComplete={load}
            />
            <BulkUpdateCasesDialog
              open={bulkUpdateOpen}
              onOpenChange={setBulkUpdateOpen}
              onUpdateComplete={load}
            />
          </>
        )}
        {isHR && (
          <CreateCaseDialog
            teamMembers={teamMembers}
            taxonomy={taxonomy}
            hrPartners={hrPartners}
            currentUserId={user?.id}
            currentUserIsHR={isHR}
            onCreated={load}
          />
        )}
        {canExport && (
          <DropdownMenu open={exportMenuOpen} onOpenChange={setExportMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                disabled={exporting}
                data-testid="export-menu-trigger"
                className="h-9 rounded-lg text-sm font-medium"
                style={{
                  borderColor: "var(--border-strong)",
                  color: "var(--page-fg)",
                  backgroundColor: "var(--surface)",
                }}
              >
                <Download className="mr-1 h-3.5 w-3.5" strokeWidth={1.8} />
                {exporting ? "Exporting…" : "Export"}
                <ChevronDown className="ml-1 h-3.5 w-3.5" strokeWidth={1.8} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-44"
              style={{
                backgroundColor: "var(--surface)",
                borderColor: "var(--border-subtle)",
                boxShadow: "var(--shadow-modal)",
              }}
            >
              <DropdownMenuItem
                data-testid="export-csv-button"
                onSelect={() => handleExport("csv")}
                className="cursor-pointer text-sm"
              >
                <Download className="mr-2 h-4 w-4" />
                CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="export-xlsx-button"
                onSelect={() => handleExport("xlsx")}
                className="cursor-pointer text-sm"
              >
                <Download className="mr-2 h-4 w-4" />
                Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        </div>
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-3">
        {isHR && (
          <KpiCard
            title="My Active Cases"
            value={myActiveCount}
            icon={Briefcase}
            accent="#1E3A5F"
            active={filter.mine}
            onClick={toggleMine}
            testid="kpi-my-active-cases"
          />
        )}
        {canSeeHighRisk && (
          <KpiCard
            title="High-Risk Queue"
            value={highRiskCount}
            icon={ShieldAlert}
            accent="#B91C1C"
            active={filter.highRisk}
            onClick={toggleHighRisk}
            testid="kpi-high-risk-queue"
          />
        )}
        <div
          className="brand-card flex flex-1 items-center gap-2 px-5 py-4"
        >
          <div className="flex-1">
            <p
              className="text-[10px] font-semibold uppercase tracking-[0.10em]"
              style={{ color: "var(--page-fg-secondary)" }}
            >
              Total in view
            </p>
            <p
              className="mt-0.5 text-2xl font-semibold tabular-nums"
              style={{ color: "var(--page-fg)", letterSpacing: "-0.01em" }}
            >
              {filtered.length}
            </p>
          </div>
          <p
            className="text-xs tabular-nums"
            style={{ color: "var(--page-fg-muted)" }}
          >
            of {cases.length}
          </p>
        </div>
      </div>

      <div className="brand-card mb-4 flex flex-wrap items-center gap-3 p-3">
        {canSeeArchived && (
          <button
            type="button"
            data-testid="cases-include-archived-toggle"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              // The "Show archived" toggle includes BOTH archived + active.
              // Clears the legacy `archived=true` (archived-only) view so we
              // never have both params set.
              if (includeArchivedView) {
                next.delete("include_archived");
              } else {
                next.delete("archived");
                next.set("include_archived", "true");
              }
              setSearchParams(next);
            }}
            className="rounded-md px-3 py-1.5 text-xs font-medium transition"
            style={{
              backgroundColor: includeArchivedView ? "var(--surface-2)" : "transparent",
              color: includeArchivedView ? "var(--page-fg)" : "var(--page-fg-secondary)",
              border: "1px solid var(--border-subtle)",
            }}
            title="Show archived cases alongside active ones. Archived rows are tagged in the list."
          >
            {includeArchivedView ? "Hide archived" : "Show archived"}
          </button>
        )}
        {canSeeArchived && archivedView && (
          <button
            type="button"
            data-testid="cases-archived-toggle"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("archived");
              setSearchParams(next);
            }}
            className="rounded-md px-3 py-1.5 text-xs font-medium transition"
            style={{
              backgroundColor: "var(--surface-2)",
              color: "var(--page-fg)",
              border: "1px solid var(--border-subtle)",
            }}
            title="Currently showing ONLY archived cases. Click to clear."
          >
            Archived only (showing) ·  clear
          </button>
        )}
        <Input
          placeholder="Search by case ID, team member, issue…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-9 w-72 rounded-lg text-sm"
          data-testid="cases-search-input"
        />
        <Select
          value={filter.status || "all"}
          onValueChange={(v) => setFilter((f) => ({ ...f, status: v === "all" ? "" : v }))}
        >
          <SelectTrigger className="h-9 w-48 rounded-lg" data-testid="cases-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Phase 2 (Feb 2026 · v2) — Unassigned filter chip. Toggles the
             ``assigned_hr_partner_id=__none__`` URL param which the list
             query interprets as "cases with no partner". */}
        <button
          type="button"
          data-testid="cases-unassigned-filter-toggle"
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            if (filter.assigned_hr_partner_id === "__none__") {
              next.delete("assigned_hr_partner_id");
            } else {
              next.set("assigned_hr_partner_id", "__none__");
            }
            setSearchParams(next);
          }}
          className="rounded-md px-3 py-1.5 text-xs font-medium transition"
          style={{
            backgroundColor:
              filter.assigned_hr_partner_id === "__none__" ? "var(--surface-2)" : "transparent",
            color:
              filter.assigned_hr_partner_id === "__none__" ? "var(--page-fg)" : "var(--page-fg-secondary)",
            border: "1px solid var(--border-subtle)",
          }}
          title="Show only cases with no HR partner assigned"
        >
          {filter.assigned_hr_partner_id === "__none__" ? "Unassigned only ✓" : "Unassigned only"}
        </button>
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            data-testid="clear-filters-button"
            style={{ color: "var(--page-fg-secondary)" }}
          >
            <X className="mr-1 h-3.5 w-3.5" />
            Clear filters
          </Button>
        )}
      </div>

      {isHR && <MyStaleBanner currentUserId={user?.id} />}

      <div className="brand-card overflow-hidden">
        <Table data-testid="cases-table">
          <TableHeader>
            <TableRow
              className="brand-table-header hover:bg-transparent"
              style={{ borderBottom: "1px solid var(--border-subtle)" }}
            >
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Case ID</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Team Member</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Service Line</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Issue</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Status</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Assigned HR</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Date Reported</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <>
                {Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={8} className="py-3">
                      <div className="flex items-center gap-4">
                        <div className="skeleton-shimmer h-3 w-24" />
                        <div className="skeleton-shimmer h-3 w-32" />
                        <div className="skeleton-shimmer h-3 w-16" />
                        <div className="skeleton-shimmer h-3 w-40" />
                        <div className="skeleton-shimmer h-3 w-20" />
                        <div className="skeleton-shimmer ml-auto h-3 w-16" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-2xl"
                      style={{
                        backgroundColor: "var(--surface-2)",
                        border: "1px solid var(--border-subtle)",
                      }}
                    >
                      <Briefcase
                        className="h-5 w-5"
                        strokeWidth={1.5}
                        style={{ color: "var(--page-fg-muted)" }}
                      />
                    </div>
                    <p
                      className="text-sm font-medium"
                      style={{ color: "var(--page-fg)" }}
                    >
                      No cases match your filters.
                    </p>
                    <p
                      className="-mt-1 text-xs"
                      style={{ color: "var(--page-fg-secondary)" }}
                    >
                      Try clearing the search or adjusting filters above.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filtered.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid={`case-row-${c.case_id}`}
                  className="row-hoverable transition-colors"
                  style={{ borderBottom: "1px solid var(--border-subtle)" }}
                >
                  <TableCell
                    className="font-mono text-xs font-medium tabular-nums"
                    style={{ color: "var(--page-fg)" }}
                  >
                    <Link to={`/cases/${c.id}`} className="hover:underline">
                      {c.case_id}
                    </Link>
                    {c.archived && (
                      <span
                        className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider"
                        style={{
                          backgroundColor: "var(--surface-2)",
                          color: "var(--page-fg-muted)",
                          border: "1px solid var(--border-subtle)",
                        }}
                        data-testid={`case-archived-badge-${c.case_id}`}
                        title="This case is archived. Open it for restore."
                      >
                        Archived
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span
                        className="font-medium"
                        style={{ color: "var(--page-fg)" }}
                      >
                        {c.team_member_name || "—"}
                      </span>
                      <span
                        className="text-xs tabular-nums"
                        style={{ color: "var(--page-fg-muted)" }}
                      >
                        #{c.team_member_position_id || c.team_member_file_number}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell
                    className="text-sm"
                    style={{ color: "var(--page-fg-secondary)" }}
                  >
                    {c.team_member_service_line || "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span
                          className="text-sm"
                          style={{ color: "var(--page-fg)" }}
                        >
                          {c.issue_type}
                        </span>
                        {c.high_risk && <HighRiskBadge />}
                      </div>
                      <span
                        className="text-xs"
                        style={{ color: "var(--page-fg-muted)" }}
                      >
                        {c.specific_issue}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusPill status={c.status} />
                  </TableCell>
                  <TableCell
                    className="text-sm"
                    style={{ color: "var(--page-fg-secondary)" }}
                  >
                    {c.assigned_hr_partner_name || (
                      <span
                        style={{ color: "var(--page-fg-muted)" }}
                        data-testid={`case-${c.case_id}-unassigned`}
                      >
                        Unassigned
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    className="text-sm tabular-nums"
                    style={{ color: "var(--page-fg-muted)" }}
                  >
                    {formatDate(c.date_reported)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      asChild
                      variant="ghost"
                      size="sm"
                      data-testid={`view-case-${c.case_id}`}
                      className="font-medium"
                      style={{ color: "var(--page-fg)" }}
                    >
                      <Link to={`/cases/${c.id}`}>Open</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
